import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import TenantLayout from '../components/TenantLayout';
import { useSite } from '../context/SiteContext';

const TenantSettings = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('tickets');
  const { sites, selectedSite: globalSelectedSite, updateTrigger } = useSite();
  const [selectedSite, setSelectedSite] = useState(null);
  const [settings, setSettings] = useState({});
  const [brandingSettings, setBrandingSettings] = useState({
    headerText: '',
    footerText: '',
    contactNumber: '',
    website: '',
    address: '',
    ticketWidth: '100mm'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const siteId = searchParams.get('site');
    if (siteId) {
      fetchSiteSettings(siteId);
    } else if (globalSelectedSite !== 'all' && sites.length > 0) {
      const site = sites.find(s => s._id === globalSelectedSite);
      if (site) {
        setSelectedSite(site);
        fetchSiteSettings(globalSelectedSite);
      }
    } else if (sites.length > 0 && !selectedSite) {
      setSelectedSite(sites[0]);
      fetchSiteSettings(sites[0]._id);
    }
  }, [searchParams, globalSelectedSite, sites, updateTrigger]);

  const fetchSiteSettings = async (siteId) => {
    const site = sites.find(s => s._id === siteId);
    if (site) {
      setSelectedSite(site);
      setSettings({
        ticketValidity: site.ticketValidity || 24,
        maxTickets: site.maxTickets || 10,
        advanceBooking: site.advanceBooking || 30,
        operatingHours: {
          start: site.operatingHours?.openTime || '06:00',
          end: site.operatingHours?.closeTime || '20:00'
        },
        weeklyOffDays: site.weeklyOffDays || [],
        printSettings: {
          ticketSize: site.printSettings?.ticketSize || '80mm x 50mm',
          paperType: site.printSettings?.paperType || 'Thermal Paper',
          printQuality: site.printSettings?.printQuality || 'High (300 DPI)',
          defaultPrinter: site.printSettings?.defaultPrinter || 'Counter 1 - Thermal Printer',
          autoPrint: site.printSettings?.autoPrint || true,
          printCopies: site.printSettings?.printCopies || 2
        },
        notifications: {
          email: site.notifications?.email || true,
          sms: site.notifications?.sms || true,
          whatsapp: site.notifications?.whatsapp || false,
          dailyReports: site.notifications?.dailyReports || true,
          lowStockAlerts: site.notifications?.lowStockAlerts || true,
          systemMaintenance: site.notifications?.systemMaintenance || true
        }
      });
      
      // Set branding settings
      setBrandingSettings({
        headerText: site.headerText || `Welcome to ${site.name}`,
        footerText: site.footerText || 'Thank you for visiting! Have a wonderful day!',
        contactNumber: site.contact?.phone || '+91 9876543210',
        website: site.website || '',
        address: site.address || '',
        ticketWidth: site.ticketWidth || '100mm'
      });
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedSite) return;
    
    const formData = new FormData();
    formData.append('logo', file);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/sites/${selectedSite._id}/logo`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
            'x-tenant-id': selectedSite.tenantId
          }
        }
      );
      
      // Update selected site with new logo
      setSelectedSite(prev => ({ ...prev, logo: response.data.logoUrl }));
      alert('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Error uploading logo. Please try again.');
    }
  };

  const saveSettings = async () => {
    if (!selectedSite) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const allSettings = {
        ...brandingSettings,
        ...settings
      };
      await axios.put(
        `${API_BASE_URL}/api/sites/${selectedSite._id}/settings`, 
        allSettings,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'x-tenant-id': selectedSite.tenantId
          }
        }
      );
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };



  const tabs = [
    { id: 'tickets', label: 'Ticket Configuration', icon: '🎫' },
    { id: 'printing', label: 'Print Settings', icon: '🖨️' },
    { id: 'branding', label: 'Branding & Design', icon: '🎨' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'payments', label: 'Payment Gateways', icon: '💳' }
  ];

  return (
    <TenantLayout userRole="tenant_admin">
      <header className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-700 text-white px-8 py-8 shadow-xl">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold mb-2">Site Settings</h1>
            <p className="text-green-100">Configure site-specific settings and preferences</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
            <span className="text-sm font-medium">Tenant Admin Only</span>
          </div>
        </div>
      </header>

      <main className="flex-1 p-8 overflow-auto">
        <div className="space-y-8 -mt-4">


          {selectedSite && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Settings for {selectedSite.name}</h2>
              <p className="text-gray-600">{selectedSite.city} • {selectedSite.features?.join(', ')}</p>
            </div>
            <div className="flex space-x-1 bg-gray-100 rounded-xl p-1 mb-8">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span className="hidden md:block">{tab.label}</span>
                </button>
              ))}
            </div>

            {activeTab === 'tickets' && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Ticket Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ticket Validity (Hours)</label>
                      <input 
                        type="number" 
                        value={settings.ticketValidity || 24} 
                        onChange={(e) => setSettings({...settings, ticketValidity: parseInt(e.target.value)})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Max Tickets Per Booking</label>
                      <input 
                        type="number" 
                        value={settings.maxTickets || 10}
                        onChange={(e) => setSettings({...settings, maxTickets: parseInt(e.target.value)})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Advance Booking Days</label>
                      <input 
                        type="number" 
                        value={settings.advanceBooking || 30}
                        onChange={(e) => setSettings({...settings, advanceBooking: parseInt(e.target.value)})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" 
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Operating Hours</label>
                      <div className="grid grid-cols-2 gap-2">
                        <input 
                          type="time" 
                          value={settings.operatingHours?.start || '09:00'} 
                          onChange={(e) => setSettings({...settings, operatingHours: {...settings.operatingHours, start: e.target.value}})}
                          className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" 
                        />
                        <input 
                          type="time" 
                          value={settings.operatingHours?.end || '18:00'} 
                          onChange={(e) => setSettings({...settings, operatingHours: {...settings.operatingHours, end: e.target.value}})}
                          className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" 
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Weekly Off Days</label>
                      <div className="flex flex-wrap gap-2">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                          <label key={day} className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              className="rounded"
                              checked={settings.weeklyOffDays?.includes(day) || false}
                              onChange={(e) => {
                                const days = settings.weeklyOffDays || [];
                                if (e.target.checked) {
                                  setSettings({...settings, weeklyOffDays: [...days, day]});
                                } else {
                                  setSettings({...settings, weeklyOffDays: days.filter(d => d !== day)});
                                }
                              }}
                            />
                            <span className="text-sm">{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}



            {activeTab === 'printing' && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Print Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ticket Size</label>
                      <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                        <option>80mm x 50mm (Standard)</option>
                        <option>100mm x 60mm (Large)</option>
                        <option>60mm x 40mm (Small)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Paper Type</label>
                      <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                        <option>Thermal Paper</option>
                        <option>Regular Paper</option>
                        <option>Card Stock</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Print Quality</label>
                      <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                        <option>High (300 DPI)</option>
                        <option>Medium (200 DPI)</option>
                        <option>Draft (150 DPI)</option>
                      </select>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Default Printer</label>
                      <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                        <option>Counter 1 - Thermal Printer</option>
                        <option>Counter 2 - Laser Printer</option>
                        <option>Main Office - Inkjet Printer</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Auto Print</label>
                      <div className="flex items-center space-x-3">
                        <input type="checkbox" defaultChecked className="rounded" />
                        <span className="text-sm text-gray-700">Automatically print after booking confirmation</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Print Copies</label>
                      <input type="number" defaultValue="2" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'branding' && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Branding & Design</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
                      <input 
                        type="text" 
                        value={selectedSite?.name || ''} 
                        readOnly
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
                      <input 
                        type="tel" 
                        value={brandingSettings.contactNumber} 
                        onChange={(e) => setBrandingSettings({...brandingSettings, contactNumber: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                      <input 
                        type="url" 
                        value={brandingSettings.website} 
                        onChange={(e) => setBrandingSettings({...brandingSettings, website: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                      <textarea 
                        rows="3" 
                        value={brandingSettings.address} 
                        onChange={(e) => setBrandingSettings({...brandingSettings, address: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      ></textarea>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Logo Upload</label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        {selectedSite?.logo ? (
                          <div>
                            <img src={selectedSite.logo} alt="Site Logo" className="w-20 h-20 mx-auto mb-2 object-contain" />
                            <button 
                              onClick={() => document.getElementById('logo-upload').click()}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              Change Logo
                            </button>
                          </div>
                        ) : (
                          <div onClick={() => document.getElementById('logo-upload').click()} className="cursor-pointer">
                            <span className="text-4xl mb-2 block">🖼️</span>
                            <p className="text-gray-600">Click to upload logo</p>
                            <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB</p>
                          </div>
                        )}
                        <input 
                          id="logo-upload"
                          type="file" 
                          accept="image/*" 
                          className="hidden"
                          onChange={handleLogoUpload}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Header Text</label>
                      <input 
                        type="text" 
                        value={brandingSettings.headerText} 
                        onChange={(e) => setBrandingSettings({...brandingSettings, headerText: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Footer Text</label>
                      <input 
                        type="text" 
                        value={brandingSettings.footerText} 
                        onChange={(e) => setBrandingSettings({...brandingSettings, footerText: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ticket Width</label>
                      <select 
                        value={brandingSettings.ticketWidth} 
                        onChange={(e) => setBrandingSettings({...brandingSettings, ticketWidth: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      >
                        <option value="80mm">80mm (Thermal)</option>
                        <option value="100mm">100mm (Standard)</option>
                        <option value="120mm">120mm (Wide)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Notification Settings</h3>
                <div className="space-y-4">
                  {[
                    { title: 'Email Notifications', desc: 'Send booking confirmations via email', enabled: true },
                    { title: 'SMS Notifications', desc: 'Send booking confirmations via SMS', enabled: true },
                    { title: 'WhatsApp Notifications', desc: 'Send booking confirmations via WhatsApp', enabled: false },
                    { title: 'Daily Reports', desc: 'Receive daily sales and visitor reports', enabled: true },
                    { title: 'Low Stock Alerts', desc: 'Get notified when ticket inventory is low', enabled: true },
                    { title: 'System Maintenance', desc: 'Receive system maintenance notifications', enabled: true }
                  ].map((setting, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-green-50 rounded-xl border border-green-100">
                      <div>
                        <h4 className="font-semibold text-gray-900">{setting.title}</h4>
                        <p className="text-sm text-gray-600">{setting.desc}</p>
                      </div>
                      <div className={`w-12 h-6 rounded-full relative cursor-pointer ${setting.enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                        <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 shadow-sm transition-all ${setting.enabled ? 'right-0.5' : 'left-0.5'}`}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'payments' && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Payment Gateway Settings</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <span className="text-2xl mr-3">💳</span>
                    <div>
                      <h4 className="font-semibold text-blue-900">Payment Gateway Configuration</h4>
                      <p className="text-blue-700">Configure payment gateways for processing bookings</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => window.location.href = '/tenant/payment-settings'}
                    className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all"
                  >
                    Configure Payment Gateways →
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button 
                onClick={saveSettings}
                disabled={saving}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
          )}
        </div>
      </main>
    </TenantLayout>
  );
};

export default TenantSettings;